'use client';

import { Box, Card, ProfileCardHeader } from 'src/components';
import { ReportDataIssueButton } from 'src/modules/Profile/components/Dialogs';
import { useLayoutControl } from 'src/context/layout';
import { useProfile } from 'src/context/providers/ProfileProvider';
import AddressDisplay from './AddressDisplay';
import DetailsDisplay from './DetailsDisplay';
import { hasContent } from 'src/utils/hasContent/hasContent';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import React, { useMemo } from 'react';

const ProfileDetails = () => {
  const { entityDetails } = useProfile();
  const { setShowReportDataIssueDialog } = useLayoutControl();
  const { profileType: entityProfileType } = getEntityIdentifiers(entityDetails);

  const profileType = entityDetails?.type;

  const formattedHCPDetails = useMemo(() => {
    if (!entityDetails || entityDetails.type === 'center') return [];

    const details = [];

    // NPI
    if (entityDetails.id) {
      details.push({ title: 'NPI', value: entityDetails.id });
    }

    // Name
    if (entityDetails.name) {
      details.push({ title: 'Name', value: entityDetails.name });
    }

    // Specialty
    if (entityDetails.specialty) {
      details.push({ title: 'Specialty', value: entityDetails.specialty });
    }

    // Phone
    if (entityDetails.phone) {
      details.push({ title: 'Phone', value: entityDetails.phone });
    }

    // Email
    if (entityDetails.email) {
      details.push({ title: 'Email', value: entityDetails.email });
    }

    return details;
  }, [entityDetails]);

  const clinicDetails = useMemo(() => {
    if (!entityDetails || profileType !== 'clinic') return [];

    const details = [...formattedHCPDetails];

    // Add GPO for clinics
    if (entityDetails.gpos && entityDetails.gpos.length > 0) {
      const gpoNames = entityDetails.gpos
        .map((gpo: { name?: string }) => gpo.name)
        .filter((name: string | undefined): name is string => !!name)
        .join(', ');
      if (gpoNames) {
        details.push({
          title: 'Group Purchasing Organization (GPO)',
          value: gpoNames,
        });
      }
    }

    return details;
  }, [entityDetails, profileType, formattedHCPDetails]);

  const formattedGroupDetails = useMemo(() => {
    if (!entityDetails || entityDetails.type !== 'group_pac') return [];

    const details = [];

    // Specialty
    if (entityDetails.specialty) {
      details.push({ title: 'Specialty', value: entityDetails.specialty });
    }

    // PAC ID
    if (entityDetails.pac_id) {
      details.push({ title: 'PAC ID', value: entityDetails.pac_id });
    }

    // Name
    if (entityDetails.name) {
      details.push({ title: 'Name', value: entityDetails.name });
    }

    return details;
  }, [entityDetails]);

  const displayItems = hasContent(clinicDetails)
    ? clinicDetails
    : hasContent(formattedGroupDetails)
    ? formattedGroupDetails
    : hasContent(formattedHCPDetails)
    ? formattedHCPDetails
    : null;

  const allDetails = hasContent(formattedGroupDetails);

  return (
    <Card className="p-6 w-full h-full bg-white">
      <ProfileCardHeader
        icon={profileType}
        title="Details"
        infoTooltip={true}
        tooltipView="physicianProfile"
      />
      <Box className="flex flex-col gap-4">
        <DetailsDisplay items={displayItems} allDetails={allDetails} />
        {hasContent(formattedHCPDetails) && (
          <>
            <AddressDisplay isPrimary={true} />
            <AddressDisplay isPrimary={false} />
          </>
        )}
        <ReportDataIssueButton
          onClick={() =>
            setShowReportDataIssueDialog({
              show: true,
              providerId: entityDetails?.provider_id,
              providerType: entityProfileType,
            })
          }
        />
      </Box>
    </Card>
  );
};

export default ProfileDetails;
